import React, { useRef, useState, useCallback, useEffect } from "react";
import { Upload, Loader2, ImageIcon, Link2, X, Move, Maximize2, Minimize2, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Aspect ratio matches the catalog modality image slot (approx 3:2 landscape).
// This gives the user a truthful preview of what will appear on the printed page.
const PREVIEW_ASPECT = 3 / 2;

const isVideoUrl = (url) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url || "");

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function ModalityImageEditor({
  value,
  onChange,
  focusX = 50,
  focusY = 50,
  fit = "cover",
  zoom = 100,
  onFocusChange,
  onFitChange,
  onZoomChange,
}) {
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [dragging, setDragging] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form);
      onChange(`${BACKEND_URL}${data.url}`);
      // Reset focal point + zoom to defaults when a new media is uploaded
      onFocusChange?.(50, 50);
      onZoomChange?.(100);
      toast.success(file.type.startsWith("video/") ? "Vídeo enviado" : "Imagem enviada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no envio");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // --- Drag handlers ------------------------------------------------------
  const updateFocusFromPointer = useCallback(
    (clientX, clientY) => {
      const el = previewRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((clientY - rect.top) / rect.height) * 100, 0, 100);
      onFocusChange?.(Math.round(x), Math.round(y));
    },
    [onFocusChange]
  );

  const onPointerDown = (e) => {
    if (!value || fit === "contain") return;
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFocusFromPointer(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    updateFocusFromPointer(e.clientX, e.clientY);
  };
  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard nudges for precision
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!value || fit === "contain") return;
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowLeft") dx = -2;
      else if (e.key === "ArrowRight") dx = 2;
      else if (e.key === "ArrowUp") dy = -2;
      else if (e.key === "ArrowDown") dy = 2;
      else return;
      e.preventDefault();
      onFocusChange?.(clamp(focusX + dx, 0, 100), clamp(focusY + dy, 0, 100));
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [focusX, focusY, value, fit, onFocusChange]);

  return (
    <div className="space-y-2" data-testid="modality-image-editor">
      <div className="flex items-center justify-between">
        <span className="font-body text-[0.62rem] text-[var(--nude)] uppercase tracking-widest flex items-center gap-1.5">
          Mídia da modalidade
          <span className="text-[var(--muted)] normal-case tracking-normal lowercase">
            (imagem ou vídeo, opcional)
          </span>
          {isVideoUrl(value) && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[var(--gold)] text-[var(--gold)] normal-case tracking-normal text-[0.55rem]">
              <Film size={10} strokeWidth={1.8} /> vídeo
            </span>
          )}
        </span>
        {value && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="fit-cover-btn"
              onClick={() => onFitChange?.("cover")}
              className={`flex items-center gap-1 px-2 py-1 border text-[0.58rem] uppercase tracking-widest font-body transition-colors ${
                fit === "cover"
                  ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(212,175,55,0.08)]"
                  : "border-[rgba(212,175,55,0.25)] text-[var(--muted)] hover:text-[var(--gold)]"
              }`}
            >
              <Maximize2 size={11} strokeWidth={1.6} /> Preencher
            </button>
            <button
              type="button"
              data-testid="fit-contain-btn"
              onClick={() => onFitChange?.("contain")}
              className={`flex items-center gap-1 px-2 py-1 border text-[0.58rem] uppercase tracking-widest font-body transition-colors ${
                fit === "contain"
                  ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(212,175,55,0.08)]"
                  : "border-[rgba(212,175,55,0.25)] text-[var(--muted)] hover:text-[var(--gold)]"
              }`}
            >
              <Minimize2 size={11} strokeWidth={1.6} /> Caber inteira
            </button>
          </div>
        )}
      </div>

      {/* Preview / focal point picker */}
      <div
        ref={previewRef}
        tabIndex={value ? 0 : -1}
        role={value && fit !== "contain" ? "slider" : undefined}
        aria-label="Ponto focal da imagem"
        aria-valuetext={`x ${focusX}%, y ${focusY}%`}
        data-testid="image-focus-picker"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`relative w-full border border-[rgba(212,175,55,0.3)] bg-[var(--satin-black)] overflow-hidden select-none outline-none focus-visible:border-[var(--gold)] ${
          value && fit !== "contain" ? "cursor-move" : ""
        }`}
        style={{ aspectRatio: PREVIEW_ASPECT, touchAction: "none" }}
      >
        {value ? (
          <>
            {isVideoUrl(value) ? (
              <video
                key={value}
                src={value}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                draggable={false}
                className="w-full h-full pointer-events-none"
                style={{
                  objectFit: fit === "contain" ? "contain" : "cover",
                  objectPosition: `${focusX}% ${focusY}%`,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: `${focusX}% ${focusY}%`,
                }}
              />
            ) : (
              <img
                src={value}
                alt=""
                draggable={false}
                className="w-full h-full pointer-events-none"
                style={{
                  objectFit: fit === "contain" ? "contain" : "cover",
                  objectPosition: `${focusX}% ${focusY}%`,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: `${focusX}% ${focusY}%`,
                }}
              />
            )}
            {fit !== "contain" && (
              <>
                {/* Guide grid for framing */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/10" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/10" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/10" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/10" />
                </div>
                {/* Focus crosshair */}
                <div
                  className="absolute pointer-events-none transition-transform"
                  style={{
                    left: `${focusX}%`,
                    top: `${focusY}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                  data-testid="focus-crosshair"
                >
                  <span className="block w-6 h-6 rounded-full border-2 border-[var(--gold)] bg-black/40 shadow-[0_0_8px_rgba(212,175,55,0.6)]" />
                </div>
                {!dragging && (
                  <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5 pointer-events-none">
                    <span className="px-2 py-1 bg-black/60 backdrop-blur-sm border border-[rgba(212,175,55,0.3)] font-body text-[0.55rem] uppercase tracking-widest text-[var(--nude)] flex items-center gap-1">
                      <Move size={10} strokeWidth={1.6} />
                      Arraste para posicionar
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[var(--muted)]">
            <div className="flex items-center gap-3">
              <ImageIcon size={24} strokeWidth={1.2} />
              <span className="w-px h-6 bg-[rgba(212,175,55,0.2)]" />
              <Film size={24} strokeWidth={1.2} />
            </div>
            <span className="font-body text-[0.62rem] uppercase tracking-widest">
              Nenhuma mídia
            </span>
          </div>
        )}
      </div>

      {/* Zoom slider */}
      {value && (
        <div className="flex items-center gap-3 px-1 pt-1" data-testid="zoom-controls">
          <button
            type="button"
            data-testid="zoom-out-btn"
            onClick={() => onZoomChange?.(clamp(zoom - 10, 100, 300))}
            disabled={zoom <= 100}
            aria-label="Diminuir zoom"
            className="w-7 h-7 flex items-center justify-center border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <span className="text-sm leading-none">−</span>
          </button>
          <input
            type="range"
            min={100}
            max={300}
            step={5}
            value={zoom}
            onChange={(e) => onZoomChange?.(Number(e.target.value))}
            data-testid="zoom-slider"
            aria-label="Zoom da imagem"
            className="flex-1 h-1 accent-[var(--gold)] cursor-pointer"
          />
          <button
            type="button"
            data-testid="zoom-in-btn"
            onClick={() => onZoomChange?.(clamp(zoom + 10, 100, 300))}
            disabled={zoom >= 300}
            aria-label="Aumentar zoom"
            className="w-7 h-7 flex items-center justify-center border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <span className="text-sm leading-none">+</span>
          </button>
          <span
            data-testid="zoom-value"
            className="w-10 text-right font-body text-[0.62rem] uppercase tracking-widest text-[var(--gold)]"
          >
            {zoom}%
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="modality-image-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black transition-colors text-[0.62rem] uppercase tracking-widest font-body disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Upload size={13} strokeWidth={1.6} />
          )}
          {uploading ? "Enviando..." : value ? "Trocar mídia" : "Enviar imagem ou vídeo"}
        </button>
        <button
          type="button"
          onClick={() => setShowUrl((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-2 border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] transition-colors text-[0.62rem] uppercase tracking-widest font-body"
        >
          <Link2 size={13} strokeWidth={1.6} /> URL
        </button>
        {value && (
          <>
            <button
              type="button"
              data-testid="reset-focus-btn"
              onClick={() => {
                onFocusChange?.(50, 50);
                onZoomChange?.(100);
              }}
              className="flex items-center gap-1.5 px-3 py-2 border border-[rgba(212,175,55,0.25)] text-[var(--nude)] hover:text-[var(--gold)] transition-colors text-[0.62rem] uppercase tracking-widest font-body"
            >
              Centralizar
            </button>
            <button
              type="button"
              data-testid="modality-image-remove-btn"
              onClick={() => {
                onChange("");
                onFocusChange?.(50, 50);
                onFitChange?.("cover");
                onZoomChange?.(100);
                setShowUrl(false);
              }}
              className="flex items-center gap-1.5 px-3 py-2 border border-[rgba(212,175,55,0.25)] text-[var(--muted)] hover:text-red-400 hover:border-red-400/50 transition-colors text-[0.62rem] uppercase tracking-widest font-body"
            >
              <X size={13} strokeWidth={1.6} /> Remover
            </button>
          </>
        )}
      </div>

      {showUrl && (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full bg-black/40 border border-[rgba(212,175,55,0.3)] text-white px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--gold)]"
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
}
